import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { usePopper } from "react-popper"

const arrowPlacementClasses = {
  top: "-bottom-1",
  bottom: "-top-1",
  left: "-right-1",
  right: "-left-1",
}

const Tooltip = ({
  children,
  maxWidth,
  placement = "auto",
  text,
  textCenter = false,
  variant = "light",
  trigger = "hover",
}) => {
  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)

  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const tooltipRef = useRef(null)

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "arrow",
          options: {
            element: arrowElement,
          },
        },
      ],
    }
  )

  // Detect if device supports hover (desktop) vs touch-only (mobile)
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(hover: none)")
      setIsTouchDevice(mediaQuery.matches)

      const handleChange = (e) => setIsTouchDevice(e.matches)
      mediaQuery.addEventListener("change", handleChange)
      return () => mediaQuery.removeEventListener("change", handleChange)
    }
  }, [])

  // Close tooltip when clicking outside (for auto trigger on touch devices)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trigger === "auto" &&
        isTouchDevice &&
        isTooltipVisible &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target)
      ) {
        setIsTooltipVisible(false)
      }
    }

    if (trigger === "auto" && isTouchDevice && isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [trigger, isTouchDevice, isTooltipVisible])

  const handleClick = (e) => {
    if (trigger === "click") {
      setIsTooltipVisible((prev) => !prev)
    }
  }

  const handleTouch = (e) => {
    if (trigger === "auto" && isTouchDevice) {
      setIsTooltipVisible((prev) => !prev)
    }
  }

  const handleMouseEnter = () => {
    if (trigger === "hover" || (trigger === "auto" && !isTouchDevice)) {
      update() && setIsTooltipVisible(true)
    }
  }

  const handleMouseLeave = () => {
    if (trigger === "hover" || (trigger === "auto" && !isTouchDevice)) {
      update() && setIsTooltipVisible(false)
    }
  }

  return (
    <div data-design-system="Tooltip" role="tooltip" ref={tooltipRef}>
      <div
        ref={setReferenceElement}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchEnd={handleTouch}
        className={
          trigger === "click" || (trigger === "auto" && isTouchDevice)
            ? "cursor-pointer"
            : ""
        }
      >
        {children}
      </div>
      <div
        ref={setPopperElement}
        className={classNames(
          "z-50 w-max whitespace-pre-wrap rounded p-3 shadow-md",
          {
            "bg-gray-800 text-white": variant === "dark",
            "bg-white": variant === "light",
            invisible: !isTooltipVisible,
            "text-center": textCenter,
          }
        )}
        style={{ ...styles.popper, maxWidth }}
        {...attributes.popper}
      >
        {text}
        <div
          ref={setArrowElement}
          className={classNames(
            "absolute h-3 w-3",
            {
              "bg-gray-800": variant === "dark",
              "bg-white": variant === "light",
            },
            arrowPlacementClasses[
              attributes.popper?.["data-popper-placement"]?.split("-")[0] ||
                placement
            ]
          )}
          style={{
            ...styles.arrow,
            transform: `${styles.arrow.transform} rotate(45deg)`,
          }}
        />
      </div>
    </div>
  )
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.string,
  placement: PropTypes.oneOf([
    "auto",
    "top",
    "bottom",
    "left",
    "right",
    "top-start",
    "top-end",
    "right-start",
    "right-end",
    "bottom-start",
    "bottom-end",
    "left-start",
    "left-end",
  ]),
  text: PropTypes.node.isRequired,
  textCenter: PropTypes.bool,
  trigger: PropTypes.oneOf(["hover", "click", "auto"]),
  variant: PropTypes.oneOf(["light", "dark"]),
}

export default Tooltip
